import React, { useCallback, useEffect, useState } from "react";
import { Box, Text, Switch, VStack, HStack, Spinner } from "@chakra-ui/react";
import { useUpdateProfile } from "../api";
import { useAppSelector, useAppDispatch } from "../../../store";
import { updateUserField } from "../../../store/slices/userSlice";
import { useNavigate } from "react-router-dom";

interface PreferenceOptionProps {
  id: string;
  label: string;
  description: string;
  isChecked: boolean;
  onChange: () => void;
  isLoading: boolean;
}

const PreferenceOption: React.FC<PreferenceOptionProps> = ({
  id,
  label,
  description,
  isChecked,
  onChange,
  isLoading,
}) => (
  <Box>
    <HStack mb={1}>
      <Switch
        id={id}
        isChecked={isChecked}
        onChange={onChange}
        isDisabled={isLoading}
      />
      <Text fontWeight="medium">{label}</Text>
      {isLoading && <Spinner size="sm" />}
    </HStack>
    <Text fontSize="sm" color="gray.500" pl={10}>
      {description}
    </Text>
  </Box>
);

interface OptionType {
  id: string;
  label: string;
  description: string;
}

const Preference: React.FC = () => {
  const user = useAppSelector((state) => state.user.data);
  const dispatch = useAppDispatch();
  const [loadingPreference, setLoadingPreference] = useState<string | null>(
    null
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/dashboard/profile/me");
    }
  }, [user]);

  const updateProfileMutation = useUpdateProfile();

  const handleToggle = useCallback(
    (id: string) => {
      if (!user || !user.contentPreferences) return;

      const newPreferences = {
        ...user.contentPreferences,
        [id]: !user.contentPreferences[id],
      };

      setLoadingPreference(id);
      updateProfileMutation.mutate(
        { contentPreferences: newPreferences },
        {
          onSuccess: () => {
            dispatch(updateUserField({ contentPreferences: newPreferences }));
          },
          onSettled: () => setLoadingPreference(null),
        }
      );
    },
    [user, updateProfileMutation, dispatch]
  );

  const options: OptionType[] = [
    {
      id: "simple",
      label: "Simple and concise",
      description: "Receive brief, to-the-point explanations",
    },
    {
      id: "examples",
      label: "More Examples",
      description: "Learn through multiple practical examples",
    },
    {
      id: "stepByStep",
      label: "Step-by-step",
      description: "Get detailed, sequential instructions",
    },
    {
      id: "context",
      label: "More Context",
      description: "Receive additional background information",
    },
  ];

  if (!user || !user.contentPreferences) {
    return null; // or a loading state
  }

  return (
    <Box py={"4rem"}>
      <Text fontSize="xl" fontWeight="bold" mb={2}>
        Lessons Preference
      </Text>
      <Text fontSize="sm" color="gray.500" mb={6}>
        Customize your learning experience by selecting your preferred lesson
        styles.
      </Text>

      <VStack align="start" spacing={6}>
        {options.map((option) => (
          <PreferenceOption
            key={option.id}
            {...option}
            isChecked={user.contentPreferences[option.id]}
            onChange={() => handleToggle(option.id)}
            isLoading={loadingPreference === option.id}
          />
        ))}
      </VStack>
    </Box>
  );
};

export default Preference;
